interface EpicFhirCacheData {
	readonly emrEncounterFhirId: string;
}

interface GetCacheArgs {
	readonly summaryId: string;
}

interface UpdateCacheArgs {
	readonly summaryId: string;
	readonly data: EpicFhirCacheData;
}

/**
 * Cache for Epic FHIR data. Uses sessionStorage to store the data.
 */
// eslint-disable-next-line @typescript-eslint/no-extraneous-class -- I preferred having class to easily track all occurrences
export class EpicFhirCache {
	static readonly EPIC_FHIR_DATA_KEY_PREFIX = 'EPIC_FHIR_DATA';

	private static readonly getCacheKey = (summaryId: string): string =>
		`${EpicFhirCache.EPIC_FHIR_DATA_KEY_PREFIX}-${summaryId}`;

	static readonly get = ({ summaryId }: GetCacheArgs): EpicFhirCacheData | null => {
		try {
			const cacheKey = EpicFhirCache.getCacheKey(summaryId);
			const cachedDataStr = sessionStorage.getItem(cacheKey);

			if (!cachedDataStr) {
				return null;
			}

			return JSON.parse(cachedDataStr);
		} catch (error) {
			console.log('Error getting epic fhir cache for summaryId: ', summaryId, 'error: ', error);
			return null;
		}
	};

	static readonly updateItemOrThrow = ({ summaryId, data }: UpdateCacheArgs): void => {
		try {
			const cacheKey = EpicFhirCache.getCacheKey(summaryId);
			sessionStorage.setItem(cacheKey, JSON.stringify(data));
			console.log('Updated epic fhir cache for summaryId: ', summaryId);
		} catch (error) {
			console.log('Failed to update epic fhir cache for summaryId: ', summaryId, 'error: ', error);
			throw error;
		}
	};

	private static readonly flushCacheData = (): void => {
		try {
			const prefix = EpicFhirCache.EPIC_FHIR_DATA_KEY_PREFIX;
			Object.keys(sessionStorage)
				.filter((key) => key.startsWith(prefix))
				.forEach((key) => {
					try {
						sessionStorage.removeItem(key);
					} catch (error) {
						console.error('Failed to remove epic fhir cache for key: ', key, 'error: ', error);
					}
				});
		} catch (error) {
			console.error('Failed to remove all epic fhir cache', error);
		}
	};

	static readonly removeAllAndUpdate = ({ summaryId, data }: UpdateCacheArgs): void => {
		try {
			EpicFhirCache.flushCacheData();
			EpicFhirCache.updateItemOrThrow({ summaryId, data });
		} catch (error) {
			console.log('Failed to remove and update epic fhir cache for summaryId: ', summaryId, 'error: ', error);
		}
	};
}
